import { ReactNode } from 'react';
import Select, { MenuProps, PlaceholderProps, Props, components } from 'react-select';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';
import { theme } from '../../theme';
import { removeDiacritics } from '../others';

export type SelectPlaceholderProps = PlaceholderProps;
export type SelectMenuProps = MenuProps;

export const selectMenuStyles = {
  border: 'none',
  borderRadius: theme.spacing.d,
  color: theme.colors.actionPrimary.default,
  fontWeight: theme.typography.body.medium.regular.fontWeight,
  fontSize: theme.typography.body.medium.regular.fontSize,
  marginRight: theme.spacing.f,
  boxShadow: theme.shadows.elevationContentDialog,
  width: '100%',
  position: 'absolute' as any,
  backgroundColor: theme.colors.backgroundBasic.active,

  '& > div': {
    padding: 0,
    borderRadius: theme.spacing.d,
  },
};

export const selectNoOptionsMessageStyles = (provided: any) => ({
  ...provided,
  textAlign: 'left',
});

export const selectControlStyles = (provided: any, state: any) => ({
  ...provided,
  paddingLeft: theme.spacing.f,
  borderColor: state.isFocused ? `${theme.colors.borderMain.active} !important` : theme.colors.borderMain.default,
  boxShadow: 'none',
});

export const selectOptionStyles = (provided: any, state: any) => ({
  ...provided,
  paddingLeft: theme.spacing.f,
  backgroundColor: state.isFocused ? theme.colors.backgroundBasic.hover : theme.colors.backgroundCover.default,
  color: state.isSelected
    ? theme.colors.actionPrimary.default
    : state.isDisabled
      ? theme.colors.text.disabled
      : theme.colors.text.primary,

  '&:active': {
    color: state.isSelected ? theme.colors.actionInvertedPrimary.active : theme.colors.text.primary,
  },
});

export const selectDropdownIndicatorStyles = (provided: any, state: any) => ({
  color: theme.colors.actionPrimary.default,
  marginRight: theme.spacing.f,
  transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : '0',
});

export const selectMenuListStyles = (provided: any) => ({
  ...provided,
  paddingTop: 0,
  paddingBottom: 0,
});

export const customStyles = {
  control: selectControlStyles,
  indicatorSeparator: (provided: any, state: any) => ({
    ...provided,
    display: 'none',
  }),
  dropdownIndicator: selectDropdownIndicatorStyles,
  menu: (provided: any, state: any) => selectMenuStyles,
  menuList: selectMenuListStyles,
  option: selectOptionStyles,
  valueContainer: (styles: any) => ({
    ...styles,
    paddingLeft: 0,
  }),
  singleValue: (provided: any, state: any) => ({
    ...provided,
    color:
      state.selectProps.menuIsOpen && state.selectProps.isSearchable
        ? theme.colors.actionInvertedPrimary.active
        : theme.colors.backgroundDark.default,
  }),
  noOptionsMessage: selectNoOptionsMessageStyles,
  multiValueRemove: (provided: any, state: any) => ({
    ...provided,
    '&:hover': {
      color: theme.colors.text.primary,
      backgroundColor: theme.colors.actionInvertedPrimary.active,
    },
  }),
  clearIndicator: (provided: any) => ({
    ...provided,
    padding: '2.5px 8px',
  }),
  indicatorsContainer: (provided: any) => ({
    ...provided,
    alignItems: 'flex-start',
    paddingTop: '6px',
  }),
};

const CustomInput = (props) => {
  return <components.Input {...props} data-cy={props.selectProps['data-cy']} />;
};

const CustomOption = (props) => {
  return (
    <components.Option
      {...props}
      innerProps={{ ...props.innerProps, 'data-cy': `${props.selectProps['data-cy']}-option-${props.value}` }}
    />
  );
};

type ExtendedProps = {
  dataCy?: string;
} & Props;

export const SelectWrapper = (props: ExtendedProps) => {
  const basicFilterOption = (
    option: FilterOptionOption<unknown> & { label: ReactNode },
    inputValue: string,
  ): boolean => {
    const searchInputWithoutDiacritics = removeDiacritics(inputValue);
    const searchInput = searchInputWithoutDiacritics.toLowerCase();
    const searchInputBothNamesSplitted = searchInput.split(' ');

    const label = typeof option.label === 'string' ? option.label : `${option.label} ${option.value}`;
    const optionLabelWithoutDiacritics = removeDiacritics(label);
    const optionLabel = optionLabelWithoutDiacritics.toLowerCase();
    const optionLabelBothNamesSplitted = optionLabel.split(' ');

    return searchInputBothNamesSplitted.every((searchWord) => {
      return optionLabelBothNamesSplitted.some((optionWord) => {
        return optionWord.startsWith(searchWord);
      });
    });
  };

  return (
    <Select
      {...{ ...props, styles: { ...customStyles, ...props.styles } }}
      components={{
        Input: CustomInput,
        Option: CustomOption,
        ...(props.components || {}),
      }}
      data-cy={props.dataCy}
      filterOption={props.filterOption || basicFilterOption}
    />
  );
};
